import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import Seo from '../components/Seo';
import { Briefcase } from 'lucide-react';
import { ShieldCheck } from 'lucide-react';
import { Building2 } from 'lucide-react';
import { Users } from 'lucide-react';
import { Hospital } from 'lucide-react';
import { Droplet } from 'lucide-react';
import { Activity } from 'lucide-react';
import { HeartPulse } from 'lucide-react';
import { FileCheck } from 'lucide-react';
import { Phone } from 'lucide-react';
import { Mail } from 'lucide-react';
import { ArrowRight } from 'lucide-react';
import { CheckCircle2 } from 'lucide-react';
import { AlertTriangle } from 'lucide-react';
import { Clock } from 'lucide-react';
import { MapPin } from 'lucide-react';
import { Repeat } from 'lucide-react';
import PartnerLeadForm from '../components/PartnerLeadForm';

const AUDIENCES = [
  { id: 'workers-comp', to: '/healthcare-partners/workers-compensation', icon: Briefcase, title: "Workers' Compensation", desc: "Transportation support for injured workers traveling to authorized medical appointments, therapy, rehabilitation, diagnostic services, and other approved healthcare destinations.", cta: "Explore Workers' Compensation Transportation" },
  { id: 'insurance-tpa', to: '/healthcare-partners/insurance-tpa', icon: ShieldCheck, title: 'Insurance & TPA Partners', desc: 'A transportation provider resource for organizations coordinating authorized non-emergency healthcare transportation.', cta: 'Insurance & TPA Partnerships' },
  { id: 'nemt-brokers', to: '/healthcare-partners/nemt-brokers', icon: FileCheck, title: 'NEMT Brokers & Transportation Coordinators', desc: 'Reliable transportation capacity for organizations managing healthcare transportation networks and trip assignments.', cta: 'Work With MoveraCare' },
  { id: 'hospitals', to: '/healthcare-partners/hospital-discharge', icon: Hospital, title: 'Hospitals & Health Systems', desc: 'Transportation support for discharge, follow-up appointments, outpatient care, recurring treatment, and other appropriate non-emergency transportation needs.', cta: 'Hospital Discharge Transportation' },
  { id: 'dialysis', to: '/healthcare-partners/dialysis-centers', icon: Droplet, title: 'Dialysis Centers', desc: 'Recurring wheelchair and ambulatory transportation supporting patients traveling to and from dialysis treatment.', cta: 'Dialysis Transportation' },
  { id: 'rehab', to: '/healthcare-partners/healthcare-facilities', icon: Activity, title: 'Rehabilitation & Therapy Facilities', desc: 'Transportation for physical therapy, occupational therapy, rehabilitation appointments, and related medical services.', cta: 'Facility Transportation' },
  { id: 'snf', to: '/healthcare-partners/healthcare-facilities', icon: Building2, title: 'Skilled Nursing Facilities', desc: 'Wheelchair and ambulatory transportation for residents requiring non-emergency transportation to healthcare appointments and approved destinations.', cta: 'Facility Transportation' },
  { id: 'case-managers', to: '/healthcare-partners/case-managers', icon: Users, title: 'Case Managers & Care Coordinators', desc: 'A direct transportation resource for professionals coordinating patient care and medical appointments.', cta: 'Case Manager Resources' },
];

const WHY = [
  { icon: HeartPulse, title: 'Healthcare-Focused Operations', desc: 'Transportation workflows designed around medical appointments and healthcare coordination.' },
  { icon: Users, title: 'Wheelchair & Ambulatory Capability', desc: 'Transportation options for ambulatory passengers and wheelchair users.' },
  { icon: Phone, title: 'Responsive Dispatch', desc: 'Clear communication between transportation coordinators, facilities, patients, and dispatch.' },
  { icon: Repeat, title: 'Recurring Transportation', desc: 'Support for dialysis, rehabilitation, therapy, and other recurring medical transportation schedules.' },
  { icon: FileCheck, title: 'Trip Documentation', desc: 'Operational documentation to support authorized transportation and billing workflows.' },
  { icon: MapPin, title: 'Greater Houston Coverage', desc: "Transportation coverage based on MoveraCare's verified service area." },
  { icon: ShieldCheck, title: 'Credentialing Ready', desc: 'Provider documentation can be made available to authorized procurement, credentialing, and network-management teams.' },
  { icon: Building2, title: 'Institutional Partnerships', desc: 'Designed to support healthcare organizations and transportation coordination partners.' },
];

const HealthcarePartners = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="healthcare-partners-page">
      <Seo
        title="Healthcare & Transportation Partners Houston | MoveraCare Mobility"
        description="Non-emergency medical transportation coordination for hospitals, TPAs, NEMT brokers, Workers' Compensation programs, dialysis centers, case managers, and healthcare organizations across Greater Houston."
        path="/healthcare-partners"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Healthcare Partners', url: '/healthcare-partners' },
        ]}
      />
      {/* Hero */}
      <section className="py-16 md:py-20 bg-[#004C97]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-[#F9A825] text-[#004C97] px-4 py-1.5 rounded-full text-sm font-bold mb-6">
              <Building2 className="w-4 h-4" />
              <span>HEALTHCARE &amp; TRANSPORTATION PARTNERS</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Healthcare Transportation Built for Better Coordination
            </h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              MoveraCare Mobility works with healthcare organizations, transportation coordinators, case managers, Workers&apos; Compensation organizations, and other healthcare partners to coordinate reliable non-emergency transportation throughout our service area.
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3">
              <a href="#partner-inquiry">
                <Button size="lg" className="bg-[#F9A825] text-[#004C97] hover:bg-[#E08E1E] font-bold text-lg px-8 shadow-lg" data-testid="cta-request-partnership">
                  Request a Partnership
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
              <Link to="/procurement">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#004C97] font-semibold text-lg px-8" data-testid="cta-procurement-resources">
                  View Procurement Resources
                </Button>
              </Link>
              <Link to="/book">
                <Button size="lg" variant="outline" className="border-2 border-[#F9A825] text-[#F9A825] hover:bg-[#F9A825] hover:text-[#004C97] font-semibold text-lg px-8" data-testid="cta-request-transportation">
                  Request Transportation
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Work With */}
      <section className="py-16 bg-white" data-testid="who-we-work-with">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-4">Who We Work With</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">Purpose-built transportation coordination for the organizations that keep patients moving.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {AUDIENCES.map((a) => {
              const Icon = a.icon;
              return (
                <Card key={a.id} id={a.id} className="border-2 hover:border-[#F9A825] transition-all group" data-testid={`audience-${a.id}`}>
                  <CardHeader>
                    <div className="w-12 h-12 rounded-lg bg-[#F9A825] flex items-center justify-center mb-3">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <CardTitle className="text-[#004C97] text-lg leading-tight">{a.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4">{a.desc}</p>
                    <Link to={a.to} className="inline-flex items-center text-sm font-semibold text-[#F9A825] group-hover:text-[#E08E1E] transition-colors" data-testid={`audience-link-${a.id}`}>
                      {a.cta}
                      <ArrowRight className="ml-1 w-4 h-4" />
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Partner With Us */}
      <section className="py-16 bg-gray-50" data-testid="why-partner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-4">Transportation Coordination You Can Depend On</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {WHY.map((w) => {
              const Icon = w.icon;
              return (
                <Card key={w.title} className="border-2 border-gray-100 hover:border-[#F9A825] transition-all">
                  <CardContent className="p-5">
                    <div className="w-10 h-10 rounded-lg bg-[#F9A825]/10 flex items-center justify-center mb-3">
                      <Icon className="w-5 h-5 text-[#F9A825]" />
                    </div>
                    <h3 className="font-semibold text-[#004C97] text-sm mb-2 leading-tight">{w.title}</h3>
                    <p className="text-xs text-gray-600 leading-relaxed">{w.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <div className="text-center mt-10">
            <a href="#partner-inquiry">
              <Button size="lg" className="bg-[#F9A825] text-[#004C97] hover:bg-[#E08E1E] font-bold text-lg px-8" data-testid="cta-start-partnership">
                Start a Partnership
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Partner Lead Form */}
      <section id="partner-inquiry" className="py-16 md:py-20 bg-white scroll-mt-24" data-testid="partner-inquiry-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-3">Contract-Ready Provider Relations</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Whether you&apos;re contracting a hospital discharge program, standing up a dialysis
              transportation schedule, or onboarding MoveraCare into a broker network — this is the
              fastest way to reach our Provider Relations team.
            </p>
          </div>
          <PartnerLeadForm sourcePage="/healthcare-partners" />
        </div>
      </section>

      {/* Workflow Snapshot */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-3">Built for Claims &amp; Care Coordination</h2>
            <p className="text-gray-600">A practical referral-to-billing workflow designed for coordinators, adjusters, case managers, TPAs, hospitals, and healthcare facilities.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {['Referral / Authorization', 'Trip Scheduling', 'Dispatch Coordination', 'Patient Pickup', 'Medical Appointment', 'Return Transportation', 'Trip Completion', 'Documentation & Billing'].map((step, idx) => (
              <div key={step} className="bg-gray-50 rounded-lg p-4 border-2 border-gray-100 hover:border-[#F9A825] transition-colors">
                <div className="w-7 h-7 rounded-full bg-[#004C97] text-white flex items-center justify-center text-xs font-bold mb-2">{idx + 1}</div>
                <p className="text-sm font-semibold text-[#004C97] leading-tight">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Verified Service Area */}
      <section className="py-16 bg-[#004C97]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <MapPin className="w-10 h-10 text-[#F9A825] mx-auto mb-4" />
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Greater Houston Service Area</h2>
          <p className="text-lg text-gray-200 mb-8 max-w-3xl mx-auto">
            Verified coverage across Harris, Fort Bend, Brazoria, Montgomery, and Galveston Counties. Fort Bend County is our core operating area — including Richmond, Sugar Land, Stafford, Missouri City, and surrounding communities. Extended-distance assignments may be accepted with advance authorization.
          </p>
          <div className="flex items-center justify-center gap-2 text-gray-200 text-sm">
            <Clock className="w-4 h-4 text-[#F9A825]" />
            <span>Mon–Sat 6:00 AM – 7:00 PM · Sunday 12:00 PM – 5:00 PM</span>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-4">Ready to Partner With MoveraCare?</h2>
          <p className="text-lg text-gray-700 mb-8">Contact Provider Relations to discuss institutional transportation, procurement, credentialing, and referral workflows.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/procurement">
              <Button size="lg" className="bg-[#F9A825] text-[#004C97] hover:bg-[#E08E1E] font-bold text-lg px-8" data-testid="cta-view-procurement">
                View Procurement Portal
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="#partner-inquiry">
              <Button size="lg" variant="outline" className="border-2 border-[#004C97] text-[#004C97] hover:bg-[#004C97] hover:text-white font-semibold text-lg px-8" data-testid="cta-email-provider-relations">
                <Mail className="mr-2 w-5 h-5" />
                Contact Provider Relations
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Non-Emergency Disclaimer */}
      <section className="py-8 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-100 border-l-4 border-gray-400 rounded p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-gray-700 leading-relaxed">
              MoveraCare provides <strong>non-emergency medical transportation</strong>. We do not market our services as ambulance, emergency response, 911 transport, or stretcher ambulance care. <strong>Not for medical emergencies. Call 911 in an emergency.</strong>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HealthcarePartners;
